/**
 * A streamed (linearized) PDF opens before Apryse knows each page's size: until
 * a page's own bytes arrive it reports page 1's size for it. Saved XFDF is
 * imported on open, and Apryse flips each annotation's position using that
 * borrowed page height and never revisits it. On an RBA whose first page is
 * landscape, every highlight on a portrait page lands 180pt too high.
 *
 * Apryse fires `pagesUpdated` with `linearizedUpdate: true` once the proper
 * page sizes arrive. When a page with saved annotations changes size that way,
 * re-import those annotations' saved XFDF as <modify> so they are re-placed
 * against the real page.
 */

const XFDF_NS = "http://ns.adobe.com/xfdf/";

const pageSizeKey = (pdfDoc, pageNumber) => {
  try {
    const info = pdfDoc?.getPageInfo?.(pageNumber);
    return info ? `${info.width}x${info.height}` : null;
  } catch {
    return null;
  }
};

/**
 * Group the saved annotation elements by 1-based page number, keyed by
 * annotation name. Later commands win for a repeated name.
 */
export const savedElementsByPage = (xfdfCommands) => {
  const byPage = new Map();
  if (typeof DOMParser === "undefined" || typeof XMLSerializer === "undefined") {
    return byPage;
  }
  const parser = new DOMParser();
  const serializer = new XMLSerializer();
  (xfdfCommands || []).forEach((xfdf) => {
    if (typeof xfdf !== "string" || !xfdf.trim()) return;
    const xml = parser.parseFromString(xfdf, "application/xml");
    if (xml.getElementsByTagName("parsererror").length) return;
    Array.from(xml.getElementsByTagNameNS("*", "add")).forEach((addSection) => {
      Array.from(addSection.children).forEach((el) => {
        const name = el.getAttribute("name");
        const page = Number.parseInt(el.getAttribute("page"), 10);
        if (!name || !Number.isFinite(page) || page < 0) return;
        const pageNumber = page + 1;
        if (!byPage.has(pageNumber)) byPage.set(pageNumber, new Map());
        byPage.get(pageNumber).set(name, serializer.serializeToString(el));
      });
    });
  });
  return byPage;
};

/**
 * Call just before importing the saved XFDF, passing a promise that settles
 * when the import has finished. Sizes are recorded first so a page that
 * arrives mid-import is still re-placed. Returns a stop function; listening
 * also stops once every page has been re-placed or the document unloads.
 */
export const keepSavedAnnotationsOnRealPages = ({
  instance,
  xfdfCommands,
  importDone = Promise.resolve(),
}) => {
  const noop = () => {};
  const documentViewer = instance?.Core?.documentViewer;
  const annotationManager = instance?.Core?.annotationManager;
  const pdfDoc = documentViewer?.getDocument?.();
  if (
    !pdfDoc ||
    typeof pdfDoc.getPageInfo !== "function" ||
    typeof documentViewer.addEventListener !== "function" ||
    typeof annotationManager?.importAnnotationCommand !== "function"
  ) {
    return noop;
  }

  instance.__whgStopSavedAnnotationPlacement?.();

  const pending = savedElementsByPage(xfdfCommands);
  if (pending.size === 0) return noop;
  const sizeAtImport = new Map();
  pending.forEach((_, pageNumber) => {
    sizeAtImport.set(pageNumber, pageSizeKey(pdfDoc, pageNumber));
  });

  let stopped = false;
  let queue = Promise.resolve(importDone).catch(() => {});

  const replace = async (pageNumber) => {
    const elements = pending.get(pageNumber);
    pending.delete(pageNumber);
    if (!elements) return;
    const live = Array.from(elements.entries())
      .filter(([name]) => annotationManager.getAnnotationById?.(name))
      .map(([, xml]) => xml);
    if (live.length === 0) return;
    const command = `<xfdf xmlns="${XFDF_NS}"><add/><modify>${live.join("")}</modify><delete/></xfdf>`;
    instance.__whgImportingAnnotations = true;
    try {
      const updated = await annotationManager.importAnnotationCommand(command);
      if (Array.isArray(updated) && updated.length > 0) {
        await annotationManager.drawAnnotationsFromList?.(updated);
      }
    } catch (err) {
      console.warn(`⚠️ Could not re-place saved annotations on page ${pageNumber}:`, err);
    } finally {
      instance.__whgImportingAnnotations = false;
    }
  };

  const onPagesUpdated = (changes) => {
    if (stopped || !changes?.linearizedUpdate) return;
    const changedPages = Array.isArray(changes.contentChanged) ? changes.contentChanged : [];
    changedPages.forEach((pageNumber) => {
      if (!pending.has(pageNumber)) return;
      if (pageSizeKey(pdfDoc, pageNumber) === sizeAtImport.get(pageNumber)) return;
      queue = queue.then(() => (stopped ? undefined : replace(pageNumber)));
    });
    queue = queue.then(() => {
      if (pending.size === 0) stop();
    });
  };

  // Someone (this user, or a collaborator's change arriving over the socket)
  // changed one of them before its page arrived: the saved XFDF is now stale,
  // so leave that annotation alone.
  const onAnnotationChanged = (annotations) => {
    if (instance.__whgImportingAnnotations) return;
    (annotations || []).forEach((annot) => {
      const name = annot?.Id;
      if (!name) return;
      pending.forEach((elements, pageNumber) => {
        if (elements.delete(name) && elements.size === 0) pending.delete(pageNumber);
      });
    });
  };

  function stop() {
    if (stopped) return;
    stopped = true;
    documentViewer.removeEventListener?.("pagesUpdated", onPagesUpdated);
    documentViewer.removeEventListener?.("documentUnloaded", stop);
    annotationManager.removeEventListener?.("annotationChanged", onAnnotationChanged);
    if (instance.__whgStopSavedAnnotationPlacement === stop) {
      instance.__whgStopSavedAnnotationPlacement = null;
    }
  }

  documentViewer.addEventListener("pagesUpdated", onPagesUpdated);
  documentViewer.addEventListener("documentUnloaded", stop);
  annotationManager.addEventListener?.("annotationChanged", onAnnotationChanged);
  instance.__whgStopSavedAnnotationPlacement = stop;
  return stop;
};
